import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';

export const UncertaintySection: React.FC = () => {
  return (
    <ReportSection id="uncertainty" title="The Uncertainty Problem" subtitle="What we don't (and can't) know">
      <p className="text-gray-700 mb-6">
        Before you take those numbers too seriously, let's talk about what we <em>don't</em> know.
        Because the honest answer is: quite a lot.
      </p>

      <ReportSubsection title="Key Unknowns Ranked by Impact">
        <div className="space-y-4 mb-6">
          <div className="bg-gray-50 p-4 border border-gray-200">
            <div className="flex justify-between items-start mb-2">
              <p className="font-semibold text-gray-900">Property Appreciation</p>
              <span className="text-xs bg-gray-200 px-2 py-1">HIGH IMPACT</span>
            </div>
            <p className="text-gray-600 text-sm">
              Will Newport Beach continue at 3.5%+ real? Return to the national 2.3%? Or correct after
              the recent surge? A 2% difference in appreciation assumptions swings the outcome by ~$400K
              over 10 years.
            </p>
          </div>

          <div className="bg-gray-50 p-4 border border-gray-200">
            <div className="flex justify-between items-start mb-2">
              <p className="font-semibold text-gray-900">Stock Market Returns</p>
              <span className="text-xs bg-gray-200 px-2 py-1">HIGH IMPACT</span>
            </div>
            <p className="text-gray-600 text-sm">
              With CAPE at 40, forward returns might be 3-5% rather than the historical 7%. Or AI could
              drive another decade of strong growth. No one knows. A 2% difference here also swings
              the outcome by hundreds of thousands.
            </p>
          </div>

          <div className="bg-gray-50 p-4 border border-gray-200">
            <div className="flex justify-between items-start mb-2">
              <p className="font-semibold text-gray-900">Rental Growth & Expenses</p>
              <span className="text-xs bg-gray-200 px-2 py-1">MEDIUM IMPACT</span>
            </div>
            <p className="text-gray-600 text-sm">
              Will rents keep up with property values? Will you face major repairs? A new roof or HVAC
              system could cost $30-50K. These are more predictable than market returns, but still uncertain.
            </p>
          </div>
        </div>
      </ReportSubsection>

      <ReportSubsection title="The Track Record of Predictions">
        <p className="text-gray-700 mb-4">
          How good are experts at predicting these things? Not great, honestly:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
          <li><strong>Real estate:</strong> Few predicted the 2008 crash. Few predicted the 2020-2023 boom.
          Predictions consistently underestimate both how high prices can go and how far they can fall.</li>
          <li><strong>Stock market:</strong> The CAPE ratio has been "predicting" lower returns since 2015.
          Anyone who sold then missed one of the best decades in market history.</li>
          <li><strong>Interest rates:</strong> The Fed's own predictions have been wildly wrong. "Transitory"
          inflation, anyone?</li>
        </ul>

        <p className="text-gray-700">
          This isn't to say predictions are useless—they're useful for thinking about scenarios. But
          they're not reliable enough to bet everything on. The future is genuinely uncertain.
        </p>
      </ReportSubsection>

      <ReportSubsection title="What Could Go Wrong">
        <p className="text-gray-700 mb-4">
          Let's be explicit about the risks on each side:
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Risks of Keeping</h4>
            <ul className="space-y-2 text-gray-600 text-sm">
              <li>• Newport Beach corrects 20-30%</li>
              <li>• Difficult tenant damages property</li>
              <li>• Major repair ($50K+) needed</li>
              <li>• California adds new landlord restrictions</li>
              <li>• Personal situation changes, need liquidity</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Risks of Selling</h4>
            <ul className="space-y-2 text-gray-600 text-sm">
              <li>• Newport Beach continues 8-10% appreciation</li>
              <li>• Stock market delivers below-average returns</li>
              <li>• You sell at a temporary low</li>
              <li>• Miss tax benefits of depreciation</li>
              <li>• Regret losing the tangible asset</li>
            </ul>
          </div>
        </div>
      </ReportSubsection>
    </ReportSection>
  );
};
